import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import type { Tables } from '$/database.types';

type DecisionDetail = Pick<
  Tables<'decisions'>,
  'id' | 'issue_title' | 'status' | 'governance_score' | 'risk_score' | 'primary_framework' | 'created_at' | 'completed_at'
> & {
  users: Pick<Tables<'users'>, 'id' | 'email' | 'role' | 'brand_role'> | null;
  organizations: Pick<Tables<'organizations'>, 'id' | 'name'> | null;
};

export type AdminSessionDetail = {
  decision: DecisionDetail;
  ledger_entries: Tables<'ledger_entries'>[];
  approval_states: Tables<'approval_states'>[];
  audit_artifacts: Pick<Tables<'audit_artifacts'>, 'id' | 'artifact_type' | 'ledger_hash' | 'exportable' | 'generated_at'>[];
};

async function fetchSessionDetail(decisionId: string): Promise<AdminSessionDetail> {
  const { data, error } = await supabaseClient.functions.invoke('admin-session-detail', {
    body: { decision_id: decisionId },
  });

  if (error) throw new Error(error.message ?? 'Failed to fetch session detail');
  return data?.data as AdminSessionDetail;
}

const useAdminSessionDetail = (decisionId: string | null) =>
  useQuery({
    queryKey: ['admin-session-detail', decisionId],
    queryFn: () => fetchSessionDetail(decisionId!),
    enabled: !!decisionId,
  });

export default useAdminSessionDetail;
